import { Box, Typography, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IOrigoCard } from '../../graphql/hid/getOrigoAccount'
import { colors } from '../../const/colors'
import { getEnv } from '../../utils/env'
import { Roles } from '../../const/permissions'
import { checkRole } from '../../utils/checkRole'
import { useAppSelector } from '../../redux/store'
import {
  handleStatus,
  getStatusColor,
  handleStatusDescription,
} from '../../utils/handleMobileCardStatus'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const LABEL_SX = {
  fontSize: '11px',
  color: colors.slate,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  mb: '4px',
}

const getStatusBg = (color: string) => {
  if (color === colors.green) return colors.greenBg
  if (color === colors.red) return colors.redBg
  if (color === '#F1901D') return '#FFF8E6'
  return colors.border2
}

const MobileCard = ({ card }: { card: IOrigoCard }) => {
  const { t } = useTranslation(['hid'])
  const { roles } = useAppSelector(state => state.user)
  const {
    status,
    current,
    cardNumber,
    privilegeRequested,
    accessGranted,
    invitationCode,
    deviceId,
    origoEndpoint,
    mysolvyTicketId,
    mysolvyTicketNr,
    mysolvyTicketStatus,
  } = card

  const statusColor = getStatusColor(status, accessGranted) || colors.slate
  const statusBg = getStatusBg(statusColor)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}
      >
        <Tooltip
          title={handleStatusDescription(status, accessGranted)}
          placement="right"
          arrow
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              px: '12px',
              py: '6px',
              borderRadius: '10px',
              background: statusBg,
              cursor: 'default',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: statusColor,
                flexShrink: 0,
              }}
            />
            <Typography
              data-e2e="mobile-card-status"
              sx={{ fontSize: '13.5px', fontWeight: 700, color: statusColor }}
            >
              {handleStatus(status, accessGranted)}
            </Typography>
          </Box>
        </Tooltip>

        {!current && (
          <Box
            sx={{
              display: 'inline-flex',
              px: '10px',
              py: '3px',
              borderRadius: '8px',
              background: colors.redBg,
              border: `1px solid ${colors.redBorder}`,
            }}
          >
            <Typography
              sx={{ fontSize: '12px', fontWeight: 700, color: colors.red }}
            >
              {t('inactive')}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 18px',
        }}
      >
        <Box>
          <Typography sx={LABEL_SX}>{t('card_number')}</Typography>
          <Typography
            data-e2e="mobile-card-number"
            sx={{
              fontSize: '15px',
              fontWeight: 700,
              color: colors.navy,
              fontFamily: 'monospace',
              letterSpacing: '0.04em',
            }}
          >
            {cardNumber}
          </Typography>
        </Box>

        {mysolvyTicketNr && (
          <Box>
            <Typography sx={LABEL_SX}>{t('my_solvy_task_number')}</Typography>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 700,
                color: colors.navy,
                fontFamily: 'monospace',
                letterSpacing: '0.03em',
              }}
            >
              #{String(mysolvyTicketNr)}
            </Typography>
          </Box>
        )}
      </Box>

      {!mysolvyTicketNr && (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            p: '10px 12px',
            borderRadius: '10px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 16, color: colors.slate, flexShrink: 0, mt: '1px' }}
          />
          <Typography
            sx={{ fontSize: '12px', color: colors.navy, lineHeight: 1.5 }}
          >
            {t('no_my_solvy_number')}
          </Typography>
        </Box>
      )}
      {mysolvyTicketNr && mysolvyTicketId && (
        <Box
          component="a"
          href={`${getEnv('MYSOLVY_URL')}/ticket_details/${mysolvyTicketId}/`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '100%',
            height: '38px',
            borderRadius: '10px',
            background: colors.navy,
            fontSize: '13px',
            fontWeight: 700,
            color: '#fff',
            textDecoration: 'none',
            transition: 'background .15s',
            '&:hover': { background: colors.navyDark },
          }}
        >
          {t('mysolvy_task_link')}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Box>
      )}

      {checkRole(roles, [Roles.developer]) && (
        <Box
          sx={{
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            p: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 800,
              color: colors.slate,
              mb: '6px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            DEV info
          </Typography>
          {(
            [
              [t('active'), String(current)],
              [t('privileges_requested'), String(privilegeRequested)],
              [t('access_granted'), String(accessGranted)],
              [t('invitation_code'), invitationCode || t('generic:none')],
              [t('device_id'), deviceId || '—'],
              [
                t('device'),
                origoEndpoint
                  ? `${origoEndpoint.model}, os: ${origoEndpoint.os}`
                  : '—',
              ],
              [t('mysolvy_ticket_id'), String(mysolvyTicketId)],
              [t('mysolvy_ticket_status'), String(mysolvyTicketStatus)],
            ] as [string, string][]
          ).map(([label, value]) => (
            <Box
              key={label}
              sx={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  minWidth: 160,
                  flexShrink: 0,
                }}
              >
                {label}:
              </Typography>
              <Typography
                sx={{ fontSize: '12px', fontWeight: 700, color: colors.navy }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default MobileCard
